/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { getBranches, getCountries, sendContract, sendDocuments } from "./middle-tier-calls";
import { showMessage } from "./message-helper";
import { dialogFallback } from "./fallbackauthdialog";

/* global OfficeRuntime */

const shouldFallback = (e: any): boolean => {
  // Check for Office add-in error codes
  const code = e?.code;
  if (code) {
    return (
      code === 13012 || // API Not Supported (host does not support SSO here)
      code === 13001 || // No user is signed into Office
      code === 13002 || // User aborted consent
      code === 13006 || // Office on the Web error
      code === 13007 || // Error occurred in the authentication provider (fallback)
      code === 13008 || // Office still working on last operation
      code === 13010 // Browser zone configuration
    );
  }

  // Check for Azure AD errors in the response
  // First check direct error message
  const errorMessage = e?.message || e?.error?.message;
  if (errorMessage && typeof errorMessage === "string") {
    if (
      errorMessage.includes("AADSTS50076") || // MFA required
      errorMessage.includes("AADSTS50079") || // MFA registration required
      errorMessage.includes("AADSTS50158") || // Conditional access policy requires MFA
      errorMessage.includes("AADSTS50072") // Strong authentication required
    ) {
      return true;
    }
  }

  // Then check responseText if available
  const responseText = e?.responseText;
  if (responseText && typeof responseText === "string") {
    if (
      responseText.includes("AADSTS50076") || // MFA required
      responseText.includes("AADSTS50079") || // MFA registration required
      responseText.includes("AADSTS50158") || // Conditional access policy requires MFA
      responseText.includes("AADSTS50072") // Strong authentication required
    ) {
      return true;
    }
  }

  return false;
};

export async function fetchCountryCode(callback: (countries: any) => void) {
  try {
    const middletierToken = await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
    const response = await getCountries(middletierToken);
    callback(response);
  } catch (exception: any) {
    if (shouldFallback(exception)) {
      return dialogFallback(callback);
    }
    showMessage("EXCEPTION: " + JSON.stringify(exception));
    throw exception;
  }
}

export async function postDocuments(documents: FormData, selectedCountryCode: string, callback: (r: any) => void) {
  try {
    const middletierToken = await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
    const response = await sendDocuments(documents, middletierToken, selectedCountryCode);
    callback(response);
  } catch (exception: any) {
    if (shouldFallback(exception)) {
      return dialogFallback(callback);
    }
    showMessage("EXCEPTION: " + JSON.stringify(exception));
    throw exception;
  }
}

export async function fetchBranches(callback: (branches: any) => void) {
  try {
    const middletierToken = await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
    const response = await getBranches(middletierToken);
    callback(response);
  } catch (exception: any) {
    if (shouldFallback(exception)) {
      return dialogFallback(callback);
    }
    showMessage("EXCEPTION: " + JSON.stringify(exception));
    throw exception;
  }
}

export async function postContract(contractDocument: FormData, callback: (r: any) => void) {
  try {
    const middletierToken = await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
    const response = await sendContract(contractDocument, middletierToken);
    callback(response);
  } catch (exception: any) {
    if (shouldFallback(exception)) {
      return dialogFallback(callback);
    }
    showMessage("EXCEPTION: " + JSON.stringify(exception));
    throw exception;
  }
}
